import { Create } from '@refinedev/mui'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material'
import { useForm } from '@refinedev/react-hook-form'
import { Controller } from 'react-hook-form'
import { useGetIdentity, useList, useNotification } from '@refinedev/core'
import { useState, useEffect, useCallback, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined'
import { User } from '@/utils/auth/auth0'
import {
  OpportunityFormValues,
  OpportunityPayload,
} from '@/interfaces/opportunities'
import { StructuredQuestion } from '@/interfaces/questions'
import QuestionManager from '@/components/QuestionManager'
import OpportunityAutoFillDialog from '@/components/OpportunityAutoFillDialog'
import FundingRoundManagement, {
  type DraftFundingRound,
} from '@/components/ManageFundingRounds'
import { validateRound } from '@/utils/fundingRoundValidation'
import HighlightedTextarea from '@/components/HighlightedTextarea'
import { findQuestionInRawText } from '@/utils/questionHighlight'
import { EMAIL_REGEX } from '@/utils/validation'
import {
  validatePositiveAmount,
  validateAmountRange,
  normalizeAmounts,
  transformQuestions,
  normalizeFundName,
} from '@/utils/opportunityValidation'
import { OpportunityTag } from '@/interfaces/categories'
import { FunderField } from '@/components/opportunities/fields/FunderField'
import { IwiField } from '@/components/opportunities/fields/IwiField'
import { FlagForUpdatesField } from '@/components/opportunities/fields/FlagForUpdatesField'
import { OpportunityLinkField } from '@/components/opportunities/fields/OpportunityLinkField'
import { useFunderAutocomplete } from '@/hooks/useFunderAutocomplete'
import { useIwiSelection } from '@/hooks/useIwiSelection'
import { useReferenceOptions } from '@/hooks/useReferenceOptions'
import { filterTagOptions } from '@/utils/dataUtils'
import { getSixMonthsFromNow } from '@/utils/dataUtils'
import { format } from 'date-fns'

export const OpportunityCreate: React.FC = () => {
  const user = useGetIdentity().data as User
  const location = useLocation()
  const copiedFrom = location.state?.copiedFrom
  // Optional state set by the funder show page's "Add with references"
  // shortcut. Pre-fills the References field and opens the auto-fill
  // dialog with Extract auto-firing.
  const autoFillFromReferences = location.state?.autoFillFromReferences as
    | { funder_uuid?: string; references?: string }
    | undefined
  const { open: openNotification } = useNotification()

  const {
    saveButtonProps,
    register,
    control,
    formState: { errors },
    refineCore: { onFinish },
    getValues,
    watch,
    setValue,
  } = useForm<OpportunityFormValues>({
    warnWhenUnsavedChanges: true,
    refineCoreProps: {
      redirect: 'show',
      resource: 'opportunities',
      errorNotification: false,
      meta: {
        updated_by:
          user?.name || `${user?.given_name || ''} ${user?.family_name || ''}`,
        ...(copiedFrom?.uuid ? { copy_from_uuid: copiedFrom.uuid } : {}),
      },
    },
    defaultValues: copiedFrom
      ? {
          funder: null,
          fund_name: copiedFrom.fund_name || '',
          is_relational: copiedFrom.is_relational ?? false,
          always_open: copiedFrom.always_open ?? false,
          no_min_max_amount: copiedFrom.no_min_max_amount ?? false,
          amount: copiedFrom.amount || '',
          min_amount: copiedFrom.min_amount || '',
          max_amount: copiedFrom.max_amount || '',
          description: copiedFrom.description || '',
          criteria: copiedFrom.criteria || '',
          exclusions: copiedFrom.exclusions || '',
          timing: copiedFrom.timing || '',
          questions: Array.isArray(copiedFrom.questions)
            ? copiedFrom.questions.join('\n\n')
            : copiedFrom.questions || '',
          application_info: copiedFrom.application_info || '',
          application_link: copiedFrom.application_link || '',
          notes: copiedFrom.notes || '',
          references: copiedFrom.references || '',
          opportunity_link: copiedFrom.opportunity_link || '',
          flag_for_updates: copiedFrom.flag_for_updates || 'Needs Work',
          gambling_proceeds: copiedFrom.gambling_proceeds ?? false,
          is_international: copiedFrom.is_international ?? false,
          only_funds_registered_charities:
            copiedFrom.only_funds_registered_charities ?? false,
          general_info: copiedFrom.general_info || '',
          profile_text: copiedFrom.profile_text || '',
          tags: copiedFrom.tags || [],
          iwi: copiedFrom.iwi ?? [],
        }
      : {
          funder: null,
          is_relational: false,
          always_open: false,
          no_min_max_amount: false,
          gambling_proceeds: false,
          is_international: false,
          only_funds_registered_charities: false,
          flag_for_updates: 'Needs Work',
          general_info: '',
          min_amount: '',
          max_amount: '',
          tags: [],
          iwi: [],
          fund_name: '',
          amount: '',
          description: '',
          criteria: '',
          exclusions: '',
          timing: '',
          questions: '',
          application_info: '',
          application_link: '',
          notes: '',
          references: autoFillFromReferences?.references || '',
          opportunity_link: '',
          profile_text: '',
        },
  })

  // Use custom hooks
  const { searchQuery, setSearchQuery, autocompleteProps, currentFunder } =
    useFunderAutocomplete(
      copiedFrom?.funder_uuid ?? autoFillFromReferences?.funder_uuid,
    )
  const { iwiList, selectedIwi } = useIwiSelection(watch('iwi'))
  const referenceOptions = useReferenceOptions(watch('references'))
  const [inheritIwi, setInheritIwi] = useState<boolean>(
    copiedFrom?.inherit_iwi_from_funder ?? true,
  )
  const [autoFillOpen, setAutoFillOpen] = useState(
    Boolean(autoFillFromReferences?.references?.trim()),
  )
  const [proposedRounds, setProposedRounds] = useState<DraftFundingRound[]>([])

  const { data: categories } = useList({
    resource: 'opportunity_categories',
    config: {
      pagination: { current: 1, pageSize: 100 },
    },
  })
  const categoriesList = useMemo(
    () => (categories?.data as OpportunityTag[]) || [],
    [categories?.data],
  )

  // Structured questions state (copied from source if applicable)
  const [structuredQuestions, setStructuredQuestions] = useState<
    StructuredQuestion[]
  >(copiedFrom?.structured_questions || [])

  const [highlightRange, setHighlightRange] = useState<{
    start: number
    end: number
  } | null>(null)

  const handleActiveQuestionChange = useCallback(
    (question: StructuredQuestion | null) => {
      if (!question) {
        setHighlightRange(null)
        return
      }
      const rawText = watch('questions') || ''
      const range = findQuestionInRawText(question.question, rawText)
      setHighlightRange(range)
    },
    [watch],
  )

  // When copying, pre-select the funder once it resolves from the autocomplete
  useEffect(() => {
    if (currentFunder) {
      setValue('funder', currentFunder)
    }
  }, [currentFunder])

  // Watch form values
  const noMinMaxAmount = watch('no_min_max_amount')

  // Form submission handler with all transformations
  async function handleFormSubmit(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault()

    try {
      const values = getValues() as OpportunityFormValues

      // Validate funder
      const selectedFunder = values.funder
      if (!selectedFunder || !selectedFunder.uuid) {
        alert('Please select a funder before submitting the form.')
        return
      }

      // Validate flag_for_updates — required, matches edit-form behaviour.
      // Defaults to "Needs Work" so this only blocks if the admin actively
      // cleared the field.
      if (!values.flag_for_updates?.trim()) {
        alert('Please set a "Flag for updates" status before submitting.')
        return
      }

      // Validate amounts
      const minValidation = validatePositiveAmount(
        values.min_amount,
        'Minimum amount',
      )
      if (!minValidation.isValid) {
        alert(minValidation.errorMessage)
        return
      }

      const maxValidation = validatePositiveAmount(
        values.max_amount,
        'Maximum amount',
      )
      if (!maxValidation.isValid) {
        alert(maxValidation.errorMessage)
        return
      }

      const rangeValidation = validateAmountRange(
        values.min_amount,
        values.max_amount,
      )
      if (!rangeValidation.isValid) {
        alert(rangeValidation.errorMessage)
        return
      }

      // Validate funding rounds (same rules as ManageFundingRounds)
      for (let i = 0; i < proposedRounds.length; i++) {
        const r = proposedRounds[i]
        const result = validateRound({
          application_open_date: r.application_open_date
            ? new Date(r.application_open_date)
            : null,
          application_close_date: r.application_close_date
            ? new Date(r.application_close_date)
            : null,
          decision_date: r.decision_date ? new Date(r.decision_date) : null,
        })
        if (!result.isValid) {
          const msg =
            result.errors.application_open_date ||
            result.errors.application_close_date ||
            'invalid'
          alert(`Funding round ${i + 1}: ${msg}`)
          return
        }
      }

      // Normalize amounts
      const { min_amount, max_amount } = normalizeAmounts(
        values.min_amount,
        values.max_amount,
        values.no_min_max_amount,
      )

      // Validate tags
      const validTags =
        values.tags?.filter((tag: string) =>
          categoriesList.some((category) => category.value === tag),
        ) || []

      // Transform payload
      const { funder, ...restValues } = values
      const payload: OpportunityPayload & {
        funding_rounds?: DraftFundingRound[]
      } = {
        ...restValues,
        funder_uuid: selectedFunder.uuid,
        min_amount,
        max_amount,
        questions: transformQuestions(values.questions),
        fund_name: normalizeFundName(values.fund_name),
        is_relational: values.is_relational ?? false,
        always_open: values.always_open ?? false,
        tags: validTags,
        region_ids: null,
        draft_profile_text: null,
        structured_questions: structuredQuestions,
        inherit_iwi_from_funder: inheritIwi,
        iwi: inheritIwi ? [] : values.iwi,
        // Inactive opps don't need check-in tracking. Otherwise compute in local TZ
        // so the date reflects the user's "today", not the UTC server's.
        next_check_in_date:
          values.flag_for_updates === 'Inactive'
            ? null
            : format(getSixMonthsFromNow(), 'yyyy-MM-dd'),
        funding_rounds: proposedRounds.length > 0 ? proposedRounds : undefined,
      }

      const result = (await onFinish(payload)) as
        | { data?: { funding_rounds_error?: string | null } }
        | undefined
      const roundsError = result?.data?.funding_rounds_error
      if (roundsError) {
        openNotification?.({
          type: 'error',
          message: 'Funding rounds not saved',
          description: roundsError,
        })
      }
    } catch (error) {
      console.error('Failed to create opportunity:', error)
      const detail = (error as any)?.response?.data?.detail
      let message: string
      if (Array.isArray(detail)) {
        message = detail
          .map((e: any) => `${e.loc?.slice(-1)[0]}: ${e.msg}`)
          .join('; ')
      } else if (
        typeof detail === 'string' &&
        detail.includes('unique_fund_name_funder')
      ) {
        message =
          'An opportunity with this name already exists for this funder.'
      } else {
        message =
          (typeof detail === 'string' && detail) ||
          (error as any)?.message ||
          'Error creating opportunity. Please try again.'
      }
      openNotification?.({ message, type: 'error' })
    }
  }

  // Don't render form until autocomplete is ready
  if (!autocompleteProps.options) {
    return null
  }

  return (
    <Create
      saveButtonProps={{
        onClick: (e) => handleFormSubmit(e),
      }}
    >
      <Box
        component="form"
        sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
        autoComplete="off"
      >
        {/* Funder */}
        <FunderField
          control={control}
          autocompleteProps={autocompleteProps}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onFunderChange={(funder) => {
            if (funder) {
              if (funder.gambling_proceeds !== undefined)
                setValue('gambling_proceeds', funder.gambling_proceeds ?? false)
              if (funder.is_international !== undefined)
                setValue('is_international', funder.is_international ?? false)
              if (funder.only_funds_registered_charities !== undefined)
                setValue(
                  'only_funds_registered_charities',
                  funder.only_funds_registered_charities ?? false,
                )
            }
          }}
        />

        {/* Fund Name */}
        <TextField
          {...register('fund_name')}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          label="Fund Name"
        />

        {/* ── Internal Notes and References ── */}
        <Divider sx={{ mt: 2, mb: 2 }}>
          <Typography variant="h6">Internal Notes and References</Typography>
        </Divider>

        <FlagForUpdatesField control={control} />

        <TextField
          {...register('notes')}
          margin="normal"
          fullWidth
          multiline
          rows={4}
          InputLabelProps={{ shrink: true }}
          label="Notes"
          sx={{
            '& .MuiOutlinedInput-input': {
              overflowWrap: 'break-word',
              wordWrap: 'break-word',
              resize: 'vertical',
            },
          }}
        />
        <TextField
          {...register('email_address', {
            setValueAs: (v: string) => v?.trim() || null,
            validate: (v) =>
              !v || EMAIL_REGEX.test(v) || 'Must be a valid email address',
          })}
          error={!!(errors as any)?.email_address}
          helperText={(errors as any)?.email_address?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="email"
          label="Email Address"
          name="email_address"
        />

        <TextField
          {...register('references')}
          margin="normal"
          fullWidth
          multiline
          rows={4}
          InputLabelProps={{ shrink: true }}
          label="References"
        />
        <Box sx={{ mt: 1, mb: 2 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setAutoFillOpen(true)}
          >
            Auto-fill from URL or document
          </Button>
        </Box>
        <OpportunityAutoFillDialog
          open={autoFillOpen}
          onClose={() => setAutoFillOpen(false)}
          initialUrls={referenceOptions}
          getValues={getValues}
          setValue={setValue}
          onApplyRounds={(newRounds) =>
            setProposedRounds((prev) => [
              ...prev,
              ...newRounds.map((r) => ({
                ...r,
                estimate_date_flag: false,
              })),
            ])
          }
          autoExtract={Boolean(autoFillFromReferences?.references?.trim())}
        />

        <Typography variant="h6">
          Opportunity Link <PublicOutlinedIcon sx={{ ml: 0.25, mt: 1 }} />
        </Typography>
        <OpportunityLinkField
          control={control}
          referenceOptions={referenceOptions}
        />

        {/* ── Details ── */}
        <Divider sx={{ mt: 2, mb: 2 }}>
          <Typography variant="h6">Details</Typography>
        </Divider>

        <Box sx={{ my: 2, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h6">Hard Filters</Typography>
          <Typography variant="caption">
            Used to filter opportunities in matching.
            <br />
            Maximum Rank is 4, used to cap generic opportunities from scoring
            very high in matching.
          </Typography>
        </Box>

        <Controller
          name="gambling_proceeds"
          control={control}
          render={({ field: { value, ...field } }) => (
            <FormControlLabel
              control={<Checkbox {...field} checked={!!value} />}
              label="Gambling Proceeds"
            />
          )}
        />
        <Controller
          name="is_international"
          control={control}
          render={({ field: { value, ...field } }) => (
            <FormControlLabel
              control={<Checkbox {...field} checked={!!value} />}
              label="International"
            />
          )}
        />
        <Controller
          name="only_funds_registered_charities"
          control={control}
          render={({ field: { value, ...field } }) => (
            <FormControlLabel
              control={<Checkbox {...field} checked={!!value} />}
              label="Only Funds Registered Charities"
            />
          )}
        />
        <Controller
          name="is_relational"
          control={control}
          render={({ field: { value, ...field } }) => (
            <FormControlLabel
              control={<Checkbox {...field} checked={!!value} />}
              label="Relational"
            />
          )}
        />

        <Controller
          name="tags"
          control={control}
          render={({ field }) => (
            <Autocomplete
              multiple
              freeSolo
              value={
                field.value
                  ? field.value
                      .map((tagValue: string) =>
                        categoriesList.find((cat) => cat.value === tagValue),
                      )
                      .filter(
                        (
                          tag: OpportunityTag | undefined,
                        ): tag is OpportunityTag => tag !== undefined,
                      )
                  : []
              }
              sx={{ my: 1 }}
              onChange={(event, newValues) => {
                const newTags = newValues.map((value) =>
                  typeof value === 'string' ? value : value.value,
                )
                field.onChange(newTags)
              }}
              options={categoriesList}
              getOptionLabel={(option) =>
                typeof option === 'string' ? option : option.value
              }
              filterOptions={(options, params) =>
                filterTagOptions(options, params.inputValue)
              }
              renderOption={(props, option) => (
                <li {...props} key={option.value}>
                  <Box>
                    <Typography variant="body1" fontWeight="500">
                      {option.value}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      fontSize="0.875rem"
                    >
                      {option.description}
                    </Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  label="Tags"
                  {...params}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#757575',
                    },
                  }}
                  InputProps={{
                    ...params.InputProps,
                    style: { backgroundColor: '#fff' },
                  }}
                />
              )}
            />
          )}
        />

        <Button disabled sx={{ width: '150px' }}>
          Generate Tags
        </Button>
        <Typography variant="caption" sx={{ mb: 1, fontStyle: 'italic' }}>
          This opportunity has not been created yet. You can generate tags
          through the edit form once it has been created.
        </Typography>

        <Box sx={{ mb: 1, mt: 3 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={inheritIwi}
                onChange={(e) => {
                  setInheritIwi(e.target.checked)
                  if (e.target.checked) setValue('iwi', [])
                }}
              />
            }
            label="Inherit iwi from funder"
          />
          <IwiField
            control={control}
            iwiList={iwiList}
            selectedIwi={inheritIwi ? [] : selectedIwi}
            disabled={inheritIwi}
            onUserInput={() => setInheritIwi(false)}
          />
        </Box>

        {/* ── Amount Details ── */}
        <Divider />
        <Box sx={{ my: 2, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h6">Amount Details</Typography>
          <Typography variant="caption">
            Used to filter opportunities in matching.
            <br />
            Description should be used for Internal reference.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, my: 1 }}>
          <TextField
            {...register('amount')}
            margin="normal"
            fullWidth
            InputLabelProps={{ shrink: true }}
            label="Amount Description"
          />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Controller
              name="min_amount"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  margin="normal"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  type="number"
                  label="Minimum Amount"
                  value={field.value || ''}
                  disabled={noMinMaxAmount}
                  onWheel={(e) => (e.target as HTMLInputElement).blur()}
                />
              )}
            />
            <Controller
              name="max_amount"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  margin="normal"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  type="number"
                  label="Maximum Amount"
                  value={field.value || ''}
                  disabled={noMinMaxAmount}
                  onWheel={(e) => (e.target as HTMLInputElement).blur()}
                />
              )}
            />
          </Box>
          <Controller
            name="no_min_max_amount"
            control={control}
            render={({ field: { value, ...field } }) => (
              <FormControlLabel
                label="No minimum or maximum amount"
                disabled={
                  Number(watch('max_amount')) > 0 ||
                  Number(watch('min_amount')) > 0
                }
                control={<Checkbox {...field} checked={!!value} />}
              />
            )}
          />
        </Box>

        {/* ── Timing Details ── */}
        <Divider sx={{ my: 1 }} />
        <Box sx={{ my: 2, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h6">
            Timing Details <PublicOutlinedIcon sx={{ ml: 0.25 }} />
          </Typography>
          <Typography variant="caption">
            Description for Internal Use Only
            <br />
            Funding Rounds and/or Always Open flag used to filter opportunities
            in matching.
            <br />
            Upcoming funding rounds visible to Organisations in their match
            results as the Application Dates.
            <br />
            If always open, 'Always open' is visible in match results as the
            Application Dates.
          </Typography>
        </Box>
        <Controller
          name="always_open"
          control={control}
          render={({ field: { value, ...field } }) => (
            <FormControlLabel
              control={<Checkbox {...field} checked={!!value} />}
              label="Always Open"
            />
          )}
        />
        <TextField
          {...register('timing')}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          label="Timing Description"
        />

        <Box sx={{ mt: 3, mb: 2 }}>
          <FundingRoundManagement
            mode="draft"
            rounds={proposedRounds}
            onRoundsChange={setProposedRounds}
          />
        </Box>

        {/* ── Profile ── */}
        <Divider sx={{ my: 1 }} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Profile <PublicOutlinedIcon sx={{ ml: 0.25 }} />
        </Typography>
        <Typography variant="caption">
          Used to filter opportunities in matching.
          <br />
          Published Profile is visible to Organisations in match results.
        </Typography>
        <TextField
          {...register('profile_text')}
          margin="normal"
          fullWidth
          multiline
          minRows={8}
          maxRows={16}
          InputLabelProps={{ shrink: true }}
          label="Profile"
        />
        <Alert severity="info">
          You can generate this after you save your changes
        </Alert>

        <Box sx={{ my: 2, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h6">Profile Data</Typography>
          <Typography variant="caption">
            Used to generate a Profile for matching.
          </Typography>
        </Box>
        <TextField
          {...register('description')}
          margin="normal"
          fullWidth
          multiline
          rows={12}
          InputLabelProps={{ shrink: true }}
          label="Description"
        />
        <TextField
          {...register('criteria')}
          margin="normal"
          fullWidth
          multiline
          rows={4}
          InputLabelProps={{ shrink: true }}
          label="Criteria"
          sx={{
            '& .MuiOutlinedInput-input': {
              overflowWrap: 'break-word',
              wordWrap: 'break-word',
              resize: 'vertical',
            },
          }}
        />
        <TextField
          {...register('exclusions')}
          margin="normal"
          fullWidth
          multiline
          rows={4}
          InputLabelProps={{ shrink: true }}
          label="Exclusions"
          sx={{
            '& .MuiOutlinedInput-input': {
              overflowWrap: 'break-word',
              wordWrap: 'break-word',
              resize: 'vertical',
            },
          }}
        />

        {/* ── General Info ── */}
        <Divider sx={{ my: 1 }} />
        <Box sx={{ my: 2, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h6">
            General Info <PublicOutlinedIcon sx={{ ml: 0.25 }} />
          </Typography>
          <Typography variant="caption">
            Visible to Organisations in match results.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Information about the opportunity that should be separated from
            profile text and matching, but is also important for users to know
            about the opportunity.
          </Typography>
        </Box>
        <TextField
          {...register('general_info')}
          margin="normal"
          fullWidth
          multiline
          rows={8}
          InputLabelProps={{ shrink: true }}
          label="General Info (eg. Application process info you want to highlight)"
        />

        {/* ── Application Details ── */}
        <Divider sx={{ my: 1 }} />
        <Box sx={{ my: 2, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h6">
            Application Details <PublicOutlinedIcon sx={{ ml: 0.25 }} />
          </Typography>
          <Typography variant="caption">
            Visible to Organisations in application drafting.
            <br />
            Link to Application Page is visible to Organisations in email copy
            of application.
          </Typography>
        </Box>
        <TextField
          {...register('application_info')}
          margin="normal"
          fullWidth
          multiline
          rows={8}
          InputLabelProps={{ shrink: true }}
          label="Application Info (eg. Files to upload: )"
          sx={{
            '& .MuiOutlinedInput-input': {
              overflowWrap: 'break-word',
              wordWrap: 'break-word',
              resize: 'vertical',
            },
          }}
        />
        <TextField
          {...register('application_link')}
          margin="normal"
          fullWidth
          multiline
          rows={2}
          InputLabelProps={{ shrink: true }}
          label="Website link to the application"
          sx={{ mb: 8 }}
        />

        {/* ── Questions ── */}
        <Box
          sx={{
            my: 2,
            display: 'flex',
            flexDirection: structuredQuestions.length > 0 ? 'row' : 'column',
            gap: 2,
            justifyContent: 'space-between',
            height: structuredQuestions.length > 0 ? '600px' : 'auto',
            resize: structuredQuestions.length > 0 ? 'vertical' : 'none',
            overflow: structuredQuestions.length > 0 ? 'auto' : 'visible',
            minHeight: structuredQuestions.length > 0 ? '300px' : 'auto',
            maxHeight: structuredQuestions.length > 0 ? '1200px' : 'none',
          }}
        >
          <Box
            sx={{
              flex: 1,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }}
          >
            <Typography variant="body1" fontWeight="bold" sx={{ mb: 2 }}>
              Raw Questions
            </Typography>
            <HighlightedTextarea
              margin="normal"
              rows={structuredQuestions.length === 0 ? 20 : undefined}
              InputLabelProps={{ shrink: true }}
              label="Questions (add a blank line between each question)"
              name="questions"
              highlightRange={highlightRange}
              value={watch('questions') || ''}
              onChange={(e) => setValue('questions', e.target.value)}
              sx={{
                width: '100%',
                flex: structuredQuestions.length > 0 ? 1 : 'none',
                minHeight: 0,
                height: structuredQuestions.length > 0 ? '100%' : 'auto',
              }}
            />
          </Box>
          <Box
            sx={{
              flex: 1,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography variant="body1" fontWeight="bold" sx={{ mb: 2 }}>
              Structured Questions
            </Typography>
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              <QuestionManager
                rawQuestions={
                  watch('questions')
                    ? watch('questions')
                        .split('\n\n')
                        .filter((q: string) => q.trim())
                    : []
                }
                structuredQuestions={structuredQuestions}
                onQuestionsChange={(questions) =>
                  setStructuredQuestions(questions)
                }
                onActiveQuestionChange={handleActiveQuestionChange}
                applicationLink={watch('application_link')}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Create>
  )
}
